/**
 * Founder UAT 2026-06 — Competency CONFIGURATION surface (fully interactive).
 * - CTH master competencies (db.competencies): read-only ("Plantilla CTH").
 * - Company competencies (company_competencies): full CRUD ("De tu empresa").
 * - Assessment links: tenant-scoped REFERENCES to published Library items.
 * - Clicking any competency opens a detail dialog with permitted actions.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Target, Layers, ArrowRight, Briefcase, BookOpen, LifeBuoy, Link2, Play,
  Plus, Pencil, Archive, X,
} from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { api, formatApiError } from '@/lib/api';
import { useConfirm } from '@/components/ui/use-confirm';

const TYPES = ['technical', 'behavioral', 'knowledge', 'other'];
const EMPTY_DRAFT = { name: '', name_en: '', description: '', category: '', ctype: 'other', expected_level: '' };

export default function ClientCompetencyConfig() {
  const { t, i18n } = useTranslation();
  const isES = i18n.language?.startsWith('es');
  const L = (es, en) => (isES ? es : en);
  const navigate = useNavigate();
  const [askConfirm, confirmUi] = useConfirm();
  const [profiles, setProfiles] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [companyComps, setCompanyComps] = useState([]);
  const [links, setLinks] = useState({});
  const [library, setLibrary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [pq, setPq] = useState('');

  // B2.3 — always prefer the authoritative localized catalog name.
  const compDisplayName = (r) => {
    const code = r.competency_code || r.code;
    const cat = (catalog || []).find((c) => c.code === code);
    if (cat) return (isES && cat.name_es) || cat.name || code;
    return r.competency_name || code;
  };
  const [detail, setDetail] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [linkFor, setLinkFor] = useState(null);
  const [linkSel, setLinkSel] = useState([]);
  const [savingLink, setSavingLink] = useState(false);

  const load = () => Promise.all([
    api.get('/client/role-profiles').then((r) => r.data).catch(() => []),
    api.get('/admin/rb/competencies').then((r) => (Array.isArray(r.data) ? r.data : (r.data?.items || []))).catch(() => []),
    api.get('/client/talent-competency/company-competencies').then((r) => r.data).catch(() => []),
    api.get('/client/talent-competency/competency-links').then((r) => r.data).catch(() => ({})),
    api.get('/client/library').then((r) => (Array.isArray(r.data) ? r.data : (r.data?.items || []))).catch(() => []),
  ]).then(([ps, cat, cc, ln, lib]) => {
    setProfiles(ps || []); setCatalog(cat || []); setCompanyComps(cc || []);
    setLinks(ln || {}); setLibrary(lib || []);
  }).finally(() => setLoading(false));

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const qName = (qid) => {
    const x = library.find((y) => y.id === qid);
    return x ? ((isES && x.name_es) || x.name || x.title || qid) : qid;
  };

  const rows = useMemo(() => {
    const map = new Map();
    (catalog || []).forEach((c) => {
      const code = c.code || c.id;
      map.set(code, {
        code, scope: 'cth',
        name: (isES && c.name_es) || c.name || code,
        category: c.category || null, ctype: c.ctype || null,
        description: (isES ? c.description_es : c.description) || c.description || null,
        expected_level: null, profiles: [],
      });
    });
    (companyComps || []).forEach((c) => {
      map.set(c.id, {
        code: c.id, scope: 'company',
        name: (!isES && c.name_en) || c.name,
        category: c.category, ctype: c.ctype,
        description: c.description, expected_level: c.expected_level,
        profiles: [], raw: c,
      });
    });
    (profiles || []).forEach((p) => {
      (p.requirements || []).forEach((r) => {
        const code = r.competency_code || r.code || r.competency;
        if (!code) return;
        if (!map.has(code)) {
          map.set(code, { code, scope: 'cth', name: r.competency_name || code, category: null, ctype: null, description: null, expected_level: null, profiles: [] });
        }
        map.get(code).profiles.push({ title: p.position_title, expected: r.expected_level ?? r.expected ?? null });
      });
    });
    let out = Array.from(map.values());
    if (q.trim()) {
      const n = q.trim().toLowerCase();
      out = out.filter((r) => `${r.name} ${r.category || ''} ${r.code}`.toLowerCase().includes(n));
    }
    return out.sort((a, b) => (a.scope === 'company' ? -1 : 1) - (b.scope === 'company' ? -1 : 1)
      || (b.profiles.length - a.profiles.length) || a.name.localeCompare(b.name));
  }, [catalog, companyComps, profiles, q, isES]);

  const openCreate = () => { setEditingId(null); setDraft(EMPTY_DRAFT); setEditOpen(true); };
  const openEdit = (r) => {
    setEditingId(r.code);
    setDraft({
      name: r.raw?.name || r.name, name_en: r.raw?.name_en || '',
      description: r.raw?.description || '', category: r.raw?.category || '',
      ctype: r.raw?.ctype || 'other',
      expected_level: r.raw?.expected_level != null ? String(r.raw.expected_level) : '',
    });
    setEditOpen(true);
  };
  const saveDraft = async () => {
    if (!draft.name.trim()) return toast.error(L('El nombre es obligatorio.', 'Name is required.'));
    setSaving(true);
    try {
      const body = {
        name: draft.name, name_en: draft.name_en || null, description: draft.description || null,
        category: draft.category || null, ctype: draft.ctype || 'other',
        expected_level: draft.expected_level ? Number(draft.expected_level) : null,
      };
      if (editingId) await api.patch(`/client/talent-competency/company-competencies/${editingId}`, body);
      else await api.post('/client/talent-competency/company-competencies', body);
      toast.success(editingId ? L('Competencia actualizada', 'Competency updated') : L('Competencia creada', 'Competency created'));
      setEditOpen(false); setDetail(null); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  const archiveComp = async (r) => {
    const ok = await askConfirm({
      title: L('Archivar competencia', 'Archive competency'),
      description: L('La competencia se archivará (no se elimina el historial) y sus vínculos con evaluaciones se retirarán.', 'The competency will be archived (history preserved) and its assessment links removed.'),
    });
    if (!ok) return;
    try {
      await api.delete(`/client/talent-competency/company-competencies/${r.code}`);
      toast.success(L('Competencia archivada', 'Competency archived'));
      setDetail(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const openLink = (r) => { setLinkSel(links[r.code] || []); setLinkFor(r); };

  // B1.2 deep-link: /competencias?link=<code>[&queue=a,b][&return=evaluar]
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkedRef = React.useRef(false);
  useEffect(() => {
    if (deepLinkedRef.current || loading) return;
    const code = searchParams.get('link');
    if (!code) return;
    const row = rows.find((r) => r.code === code);
    if (row) {
      deepLinkedRef.current = true;
      openLink(row);
      toast.info(L(`Vinculando evaluaciones para "${row.name}".`, `Linking assessments for "${row.name}".`));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, rows, searchParams]);

  const advanceDeepLinkQueue = (justLinkedCode) => {
    const queue = (searchParams.get('queue') || '').split(',').filter(Boolean)
      .filter((c) => c !== justLinkedCode && !(links[c] || []).length);
    const ret = searchParams.get('return');
    if (queue.length) {
      const nextRow = rows.find((r) => r.code === queue[0]);
      if (nextRow) {
        setSearchParams({ link: queue[0], queue: queue.join(','), ...(ret ? { return: ret } : {}) }, { replace: true });
        openLink(nextRow);
        return;
      }
    }
    if (ret === 'evaluar') {
      toast.success(L('Vínculos completados — regresando a la evaluación.', 'Linking complete — returning to the evaluation.'));
      navigate('/client/talentcompetency');
    }
  };
  const saveLink = async () => {
    if (!linkFor) return;
    setSavingLink(true);
    try {
      const { data } = await api.put(`/client/talent-competency/competency-links/${encodeURIComponent(linkFor.code)}`, { questionnaire_ids: linkSel });
      setLinks((m) => ({ ...m, [linkFor.code]: data.questionnaire_ids }));
      toast.success(L('Vínculos guardados', 'Links saved'));
      const code = linkFor.code;
      setLinkFor(null);
      if (searchParams.get('link')) advanceDeepLinkQueue(code);
    } catch (e) { toast.error(formatApiError(e)); } finally { setSavingLink(false); }
  };
  const unlinkOne = async (r, qid) => {
    const next = (links[r.code] || []).filter((x) => x !== qid);
    try {
      await api.put(`/client/talent-competency/competency-links/${encodeURIComponent(r.code)}`, { questionnaire_ids: next });
      setLinks((m) => ({ ...m, [r.code]: next }));
      toast.success(L('Evaluación desvinculada', 'Assessment unlinked'));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const scopeBadge = (r) => (
    <Badge variant="outline" className={`text-xs ${r.scope === 'company' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-sky-50 text-sky-700 border-sky-200'}`}>
      {r.scope === 'company' ? L('De tu empresa', 'Company') : L('Plantilla CTH', 'CTH template')}
    </Badge>
  );

  return (
    <>
      <Topbar
        title={L('Configurar competencias', 'Configure competencies')}
        subtitle={L('Catálogo de competencias y perfiles de puesto de tu organización', "Your organization's competency catalog and position profiles")}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/client/talentcompetency/evo')} data-testid="cc-goto-gaps" className="gap-1.5">
              <Layers className="h-4 w-4" />
              {L('Brechas de Competencias', 'Competency Gaps')}
              <ArrowRight className="h-3 w-3" />
            </Button>
            <Button size="sm" onClick={() => navigate('/client/talentcompetency')} data-testid="cc-back-tco" className="gap-1.5">
              <Target className="h-4 w-4" /> TalentCompetency
            </Button>
          </div>
        }
      />
      <div className="p-6 lg:p-8 space-y-5" data-testid="competency-config-page">
        <Card className="p-5 shadow-card border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
          <div className="grid sm:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="font-semibold flex items-center gap-1.5"><BookOpen className="h-4 w-4 text-sky-600" />{L('Competencias', 'Competencies')}</p>
              <p className="text-xs text-muted-foreground mt-1">{L('Plantillas CTH (gobernadas, solo lectura) y competencias propias de tu empresa (editables).', 'CTH templates (governed, read-only) plus your own company competencies (editable).')}</p>
            </div>
            <div>
              <p className="font-semibold flex items-center gap-1.5"><Briefcase className="h-4 w-4 text-primary" />{L('Perfiles de puesto', 'Position profiles')}</p>
              <p className="text-xs text-muted-foreground mt-1">{L('Cada perfil define las competencias requeridas y su nivel esperado.', 'Each profile defines the required competencies and expected level.')}</p>
            </div>
            <div>
              <p className="font-semibold flex items-center gap-1.5"><Layers className="h-4 w-4 text-orange-500" />{L('Brechas (medición)', 'Gaps (measurement)')}</p>
              <p className="text-xs text-muted-foreground mt-1">{L('Brechas de Competencias es la superficie de medición esperado vs. observado.', 'Competency Gaps is the expected-vs-observed measurement surface.')}</p>
            </div>
          </div>
        </Card>

        {loading ? (
          <Card className="p-10 text-center text-sm text-muted-foreground">{t('common.loading', { defaultValue: 'Loading…' })}</Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
            <Card className="p-5 shadow-card lg:col-span-2" data-testid="cc-profiles-card">
              <div className="flex items-center gap-2 mb-1">
                <Briefcase className="h-5 w-5 text-primary" />
                <h2 className="font-semibold">{L('Perfiles de puesto', 'Position profiles')}</h2>
                <Badge variant="outline" className="text-xs">{profiles.length}</Badge>
              </div>
              {profiles.length > 0 && (
                <Input value={pq} onChange={(e) => setPq(e.target.value)}
                       placeholder={L('Buscar perfil de puesto…', 'Search position profile…')}
                       className="h-8 text-sm my-2" data-testid="cc-profiles-search" />
              )}
              {profiles.length === 0 ? (
                <div className="text-center py-8" data-testid="cc-profiles-empty">
                  <p className="text-sm text-muted-foreground mb-3">{L('Aún no hay perfiles de puesto configurados.', 'No position profiles configured yet.')}</p>
                  <Button size="sm" variant="outline" onClick={() => navigate('/client/talentcompetency')} className="gap-1.5">
                    <LifeBuoy className="h-4 w-4" /> {L('Ir a TalentCompetency', 'Go to TalentCompetency')}
                  </Button>
                </div>
              ) : (
                <ul className="divide-y max-h-[30rem] overflow-auto" data-testid="cc-profiles-list">
                  {profiles.filter((p) => !pq.trim() || (p.position_title || '').toLowerCase().includes(pq.trim().toLowerCase())).map((p) => (
                    <li key={p.id} className="py-2.5" data-testid={`cc-profile-${p.id}`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">{p.position_title}</span>
                        <Badge variant="outline" className={`text-xs ${p.scope === 'global' ? 'bg-sky-50 text-sky-700 border-sky-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}>
                          {p.scope === 'global' ? L('Plantilla CTH', 'CTH template') : L('De tu empresa', 'Company')}
                        </Badge>
                        <Badge variant="outline" className="text-xs tabular-nums">{(p.requirements || []).length} {L('competencias', 'competencies')}</Badge>
                      </div>
                      {(p.requirements || []).length > 0 && (
                        <p className="text-xs text-muted-foreground mt-1 truncate">
                          {(p.requirements || []).slice(0, 4).map((r) => compDisplayName(r)).filter(Boolean).join(' · ')}
                          {(p.requirements || []).length > 4 ? ' …' : ''}
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <Card className="p-5 shadow-card lg:col-span-3" data-testid="cc-catalog-card">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <BookOpen className="h-5 w-5 text-sky-600" />
                <h2 className="font-semibold">{L('Catálogo de competencias', 'Competency catalog')}</h2>
                <Badge variant="outline" className="text-xs">{rows.length}</Badge>
                <span className="flex-1" />
                <Button size="sm" onClick={openCreate} className="gap-1.5 bg-gradient-primary" data-testid="cc-create-btn">
                  <Plus className="h-4 w-4" /> {L('Crear competencia', 'Create competency')}
                </Button>
              </div>
              <Input value={q} onChange={(e) => setQ(e.target.value)}
                     placeholder={L('Buscar competencia…', 'Search competency…')}
                     className="h-8 text-sm my-2" data-testid="cc-catalog-search" />
              {rows.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center" data-testid="cc-catalog-empty">
                  {L('No hay competencias todavía. Crea la primera con "Crear competencia".', 'No competencies yet. Create the first one with "Create competency".')}
                </p>
              ) : (
                <ul className="divide-y max-h-[28rem] overflow-auto" data-testid="cc-catalog-list">
                  {rows.map((r) => (
                    <li key={r.code} className="py-2.5 px-1 rounded-md hover:bg-muted/40 cursor-pointer"
                        onClick={() => setDetail(r)} data-testid={`cc-competency-${r.code}`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">{r.name}</span>
                        {scopeBadge(r)}
                        {r.category && <Badge variant="outline" className="text-xs">{r.category}</Badge>}
                        <Badge variant="outline" className={`text-xs tabular-nums ${r.profiles.length > 0 ? 'bg-violet-50 text-violet-700 border-violet-200' : 'bg-muted text-muted-foreground'}`}>
                          {r.profiles.length} {L('perfil(es)', 'profile(s)')}
                        </Badge>
                        {(links[r.code] || []).length > 0 && (
                          <Badge variant="outline" className="text-xs tabular-nums bg-sky-50 text-sky-700 border-sky-200" data-testid={`cc-linked-badge-${r.code}`}>
                            <Link2 className="h-3 w-3 mr-1" />{(links[r.code] || []).length}
                          </Badge>
                        )}
                        <span className="flex-1" />
                        <Button size="sm" variant="outline" className="h-7 px-2 text-xs gap-1"
                                onClick={(e) => { e.stopPropagation(); openLink(r); }} data-testid={`cc-link-btn-${r.code}`}>
                          <Link2 className="h-3 w-3" /> {L('Vincular', 'Link')}
                        </Button>
                        {(links[r.code] || []).length > 0 && (
                          <Button size="sm" className="h-7 px-2 text-xs gap-1 bg-sky-600 hover:bg-sky-700"
                                  onClick={(e) => { e.stopPropagation(); navigate(`/client/talentcompetency?knowledgeAssessment=${links[r.code][0]}`); }}
                                  data-testid={`cc-evaluate-btn-${r.code}`}>
                            <Play className="h-3 w-3" /> {L('Evaluar', 'Evaluate')}
                          </Button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>
        )}
      </div>

      {/* Detail dialog — read-only for CTH, actionable for company records */}
      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <DialogContent className="max-w-lg" data-testid="cc-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 flex-wrap">
                  {detail.name} {scopeBadge(detail)}
                </DialogTitle>
                <DialogDescription>
                  {detail.scope === 'company'
                    ? L('Competencia de tu empresa — puedes editarla, vincular evaluaciones o archivarla.', 'Company competency — you can edit it, link assessments or archive it.')
                    : L('Plantilla CTH gobernada — solo lectura. Puedes vincular evaluaciones que la midan.', 'Governed CTH template — read-only. You may link assessments that measure it.')}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                {detail.description && <p className="text-muted-foreground">{detail.description}</p>}
                <div className="flex flex-wrap gap-2">
                  {detail.category && <Badge variant="outline" className="text-xs">{detail.category}</Badge>}
                  {detail.ctype && <Badge variant="outline" className="text-xs capitalize">{detail.ctype}</Badge>}
                  {detail.expected_level != null && (
                    <Badge variant="outline" className="text-xs">{L('Nivel esperado', 'Expected level')}: {detail.expected_level}</Badge>
                  )}
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase text-muted-foreground mb-1">{L('Perfiles que la usan', 'Profiles using it')} ({detail.profiles.length})</p>
                  {detail.profiles.length === 0 ? (
                    <p className="text-xs text-muted-foreground">{L('Ningún perfil de puesto la requiere todavía.', 'No position profile requires it yet.')}</p>
                  ) : (
                    <ul className="text-xs space-y-0.5">
                      {detail.profiles.map((p, i) => (
                        <li key={i}>• {p.title}{p.expected != null ? ` — ${L('esperado', 'expected')}: ${p.expected}` : ''}</li>
                      ))}
                    </ul>
                  )}
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase text-muted-foreground mb-1">{L('Evaluaciones vinculadas', 'Linked assessments')} ({(links[detail.code] || []).length})</p>
                  {(links[detail.code] || []).length === 0 ? (
                    <p className="text-xs text-muted-foreground">{L('Sin evaluaciones vinculadas. Usa "Vincular evaluación".', 'No linked assessments. Use "Link assessment".')}</p>
                  ) : (
                    <ul className="text-xs space-y-1" data-testid="cc-detail-links">
                      {(links[detail.code] || []).map((qid) => (
                        <li key={qid} className="flex items-center gap-2">
                          <Link2 className="h-3 w-3 text-sky-600 shrink-0" />
                          <span className="flex-1 truncate">{qName(qid)}</span>
                          <button type="button" className="text-destructive hover:underline text-xs inline-flex items-center gap-0.5"
                                  onClick={() => unlinkOne(detail, qid)} data-testid={`cc-unlink-${qid}`}>
                            <X className="h-3 w-3" />{L('Desvincular', 'Unlink')}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
              <DialogFooter className="flex-wrap gap-2">
                <Button variant="outline" size="sm" className="gap-1" onClick={() => { openLink(detail); }} data-testid="cc-detail-link-btn">
                  <Link2 className="h-3.5 w-3.5" /> {L('Vincular evaluación', 'Link assessment')}
                </Button>
                {(links[detail.code] || []).length > 0 && (
                  <Button size="sm" className="gap-1 bg-sky-600 hover:bg-sky-700"
                          onClick={() => navigate(`/client/talentcompetency?knowledgeAssessment=${links[detail.code][0]}`)} data-testid="cc-detail-evaluate-btn">
                    <Play className="h-3.5 w-3.5" /> {L('Evaluar', 'Evaluate')}
                  </Button>
                )}
                {detail.scope === 'company' && (
                  <>
                    <Button variant="outline" size="sm" className="gap-1" onClick={() => openEdit(detail)} data-testid="cc-detail-edit-btn">
                      <Pencil className="h-3.5 w-3.5" /> {L('Editar', 'Edit')}
                    </Button>
                    <Button variant="outline" size="sm" className="gap-1 text-destructive border-destructive/40"
                            onClick={() => archiveComp(detail)} data-testid="cc-detail-archive-btn">
                      <Archive className="h-3.5 w-3.5" /> {L('Archivar', 'Archive')}
                    </Button>
                  </>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Create / edit company competency */}
      <Dialog open={editOpen} onOpenChange={(o) => { if (!o) setEditOpen(false); }}>
        <DialogContent className="max-w-2xl" data-testid="cc-edit-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? L('Editar competencia', 'Edit competency') : L('Crear competencia', 'Create competency')}</DialogTitle>
            <DialogDescription>
              {L('Competencia propia de tu empresa. Las plantillas CTH no se modifican.', 'Company-owned competency. CTH templates are never modified.')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">{L('Nombre (ES)', 'Name (ES)')} <span className="text-destructive">*</span></Label>
              <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="cc-draft-name" autoFocus />
            </div>
            <div>
              <Label className="text-xs">{L('Nombre (EN, opcional)', 'Name (EN, optional)')}</Label>
              <Input value={draft.name_en} onChange={(e) => setDraft({ ...draft, name_en: e.target.value })} data-testid="cc-draft-name-en" />
            </div>
            <div>
              <Label className="text-xs">{L('Descripción', 'Description')}</Label>
              <Textarea rows={2} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} data-testid="cc-draft-description" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">{L('Categoría', 'Category')}</Label>
                <Input value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })} data-testid="cc-draft-category" />
              </div>
              <div>
                <Label className="text-xs">{L('Tipo', 'Type')}</Label>
                <Select value={draft.ctype} onValueChange={(v) => setDraft({ ...draft, ctype: v })}>
                  <SelectTrigger data-testid="cc-draft-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TYPES.map((tp) => (
                      <SelectItem key={tp} value={tp}>
                        {L({ technical: 'Técnica', behavioral: 'Conductual', knowledge: 'Conocimiento', other: 'Otra' }[tp],
                          { technical: 'Technical', behavioral: 'Behavioral', knowledge: 'Knowledge', other: 'Other' }[tp])}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">{L('Nivel esperado (1–5)', 'Expected level (1–5)')}</Label>
                <Input type="number" min={1} max={5} value={draft.expected_level}
                       onChange={(e) => setDraft({ ...draft, expected_level: e.target.value })} data-testid="cc-draft-level" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} disabled={saving}>{t('common.cancel', { defaultValue: 'Cancelar' })}</Button>
            <Button onClick={saveDraft} disabled={saving} data-testid="cc-draft-save" className="bg-gradient-primary">
              {saving ? t('common.saving', { defaultValue: 'Guardando…' }) : t('common.save', { defaultValue: 'Guardar' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link assessments dialog — references only */}
      <Dialog open={!!linkFor} onOpenChange={(o) => { if (!o) setLinkFor(null); }}>
        <DialogContent className="max-w-2xl" data-testid="cc-link-dialog">
          <DialogHeader>
            <DialogTitle>{L('Vincular evaluaciones', 'Link assessments')}{linkFor ? ` — ${linkFor.name}` : ''}</DialogTitle>
            <DialogDescription>
              {L('Selecciona las evaluaciones publicadas que realmente miden esta competencia. Son referencias a la Biblioteca — no se duplican instrumentos.',
                'Select the published assessments that genuinely measure this competency. References to the Library — no instruments are duplicated.')}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-72 overflow-auto space-y-1.5 pr-1">
            {library.length === 0 ? (
              <div className="text-sm text-muted-foreground py-4 text-center space-y-1" data-testid="cc-link-empty">
                <p>{L('No hay evaluaciones publicadas compatibles en tu biblioteca.', 'No compatible published assessments in your library.')}</p>
                <p className="text-xs">{L('Crea una evaluación en tu Biblioteca o usa "Solicitar apoyo de CTH" para pedir la publicación de un instrumento que mida esta competencia.', 'Create an assessment in your Library or use "Request CTH support" to ask for a published instrument measuring this competency.')}</p>
              </div>
            ) : [...library].sort((a, b) => (((isES && a.name_es) || a.name || '') .localeCompare((isES && b.name_es) || b.name || ''))).map((qq) => (
              <label key={qq.id} className="flex items-start gap-2.5 rounded-md border border-border/70 p-2.5 cursor-pointer hover:bg-muted/40" data-testid={`cc-link-opt-${qq.id}`}>
                <Checkbox checked={linkSel.includes(qq.id)}
                          onCheckedChange={(v) => setLinkSel((s) => (v ? [...s, qq.id] : s.filter((x) => x !== qq.id)))}
                          className="mt-0.5" />
                <span className="text-sm">
                  <span className="font-medium block">{(isES && qq.name_es) || qq.name || qq.title}</span>
                  {qq.professional_area && <span className="text-xs text-muted-foreground">{qq.professional_area}</span>}
                </span>
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkFor(null)} disabled={savingLink}>{t('common.cancel', { defaultValue: 'Cancelar' })}</Button>
            <Button onClick={saveLink} disabled={savingLink} data-testid="cc-link-save" className="gap-1.5">
              <Link2 className="h-4 w-4" />
              {savingLink ? t('common.saving', { defaultValue: 'Guardando…' }) : L('Guardar vínculos', 'Save links')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {confirmUi}
    </>
  );
}
